import React, { FunctionComponent } from 'react';
import DashboardLayout from '../../components/layout/layout';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HomeIcon from '@mui/icons-material/Home';

interface CategoryData {
  icon: React.ReactNode;
  items?: string[];
  subcategories?: { [key: string]: string[] | { [key: string]: string[] } };
}

const salesToolsData: { [key: string]: CategoryData } = {
  
  "PRODUCT & MARKET REFERENCE": {
    icon: <DescriptionIcon sx={{ fontSize: 24, color: '#000000', mr: 1 }} />,
    subcategories: {
      "HYDRAULIC PRODUCTS / ENERGY PRODUCTS": {
        "VALVE": [],
        "REGULATOR": []
      },
      "INDUSTRIAL PRODUCTS": [],
      "DYNALCO BRAND PRODUCTS": [],
      "MARKET INFORMATION": [],
      "LEGACY DOCUMENTS (REFERENCE)": []
    }
  },
  "GENERAL INFORMATION": {
    icon: <BusinessIcon sx={{ fontSize: 24, color: '#000000', mr: 1 }} />,
    items: [
      "Barksdale General Overview - 2024",
      "Standard Conditions of Sale",
      "General Sales Policy",
      "Channel Partner Agreement"      
    ]
  },
  "FORMS": {
    icon: <MenuBookIcon sx={{ fontSize: 24, color: '#000000', mr: 1 }} />,
    items: [
      "FAILURE ANALYSIS FORM",
      "Valve & Switch Repair Kits"   
    ]
  }
};

const NewSalesTools: FunctionComponent = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const getCategoryDisplayName = (categoryKey: string) => {
    const displayNames: { [key: string]: string } = {
      'PRODUCT & MARKET REFERENCE': 'PRODUCT & MARKET REFERENCE',
      'GENERAL INFORMATION': 'GENERAL INFORMATION',
      'FORMS': 'FORMS / REPAIR KITS'
    };
    
    return displayNames[categoryKey] || categoryKey.replace(/_/g, ' ');
  };

  const handleCategoryClick = (categoryKey: string) => {
    navigate(`/resources/category/${categoryKey}`);
  };

  // Function to handle home navigation (refresh current page)
  const handleHomeClick = () => {
    navigate('/resources');
  };

  // Home button component for top of page
  const TopHomeButton = () => (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
      <Button
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={handleHomeClick}
        sx={{
          bgcolor: '#c8102e',
          textTransform: 'none',
          fontWeight: 600,
          ...theme.typography.button,
          '&:hover': { bgcolor: '#a30f24' },
        }}
      >
        Sales Tools Home
      </Button>
    </Box>
  );

  return (
    <DashboardLayout 
      title={"Sales Tools"} 
      initialBreadcrumbs={"Sales Tools"} 
      levelBreadcrumbs={""} 
      breadcrumbs={'Sales Tools'} 
      activeMenu={'resources'}
    >
      <TopHomeButton />
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {Object.entries(salesToolsData).map(([categoryKey, categoryData]) => (
            <Card
              key={categoryKey}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleCategoryClick(categoryKey)}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {categoryData.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {getCategoryDisplayName(categoryKey)}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
        
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default NewSalesTools;
